/**
 * this file is for serializing and deserializing the filters for the url.
 * we want to save as much space as possible. Thanks to GPT5 for creating this mess
 */

import {
  b64ToUuid,
  decodeAdaptive,
  encodeAdaptive,
  fnv1a32,
  UUID_RE,
  uuidToB64,
} from "./url-codec";

export type CodecFilter = {
  field: string;
  fieldType: string;
  operator: string;
  value: string;
  active: boolean;
  id: string;
};

// Packed tuple: [fieldIdx, typeIdx, opIdx, activeBit, encodedValue]
type Packed = [number, number, number, 0 | 1, string];

// Names used by the packed tuples. V2 lists every field, type and operator a URL uses; V1 listed
// only fields and the types missing from V1_TYPES, and has no operator list.
type Header = {
  f: string[];
  t: string[];
  o?: string[];
};

// V2 indexes only into the URL's own header, so no list in the code can change what it decodes to.
const V = 2;

// V1 stored types and operators as positions in these lists. Existing V1 links decode through
// them, so never edit, reorder or extend them.
const V1_TYPES = [
  "STRING",
  "TEXT",
  "RICHTEXT",
  "LONGTEXT",
  "JSON",
  "ID",
  "INT",
  "FLOAT",
  "ENUM",
  "DATETIME",
  "DATE",
  "TIME",
  "BOOLEAN",
];
const V1_OPERATORS = [
  "equal",
  "notEqual",
  "greaterThan",
  "lessThan",
  "greaterThanOrEqual",
  "lessThanOrEqual",
  "isNull",
  "isNotNull",
  "isEmpty",
  "isNotEmpty",
  "isTrue",
  "isFalse",
  "like",
  "ilike",
  "notiLike",
  "notLike",
  "in",
  "notIn",
  "startsWith",
  "endsWith",
  "contains",
];

// "~" prefix marks a compacted UUID token
function encodeValue(v: string, fieldType: string, op: string): string {
  if (
    op === "isNull" ||
    op === "isNotNull" ||
    op === "isEmpty" ||
    op === "isNotEmpty" ||
    op === "isTrue" ||
    op === "isFalse"
  )
    return "";

  const trimmed = v.trim();

  // JSON array (for in/notIn)
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const arr = JSON.parse(trimmed) as unknown[];
      const enc = arr.map((x) =>
        typeof x === "string" && UUID_RE.test(x) ? "~" + uuidToB64(x) : x,
      );
      return JSON.stringify(enc);
    } catch {
      // fall through
    }
  }

  // CSV
  if (v.includes(",")) {
    return v
      .split(",")
      .map((p) => {
        const s = p.trim();
        return UUID_RE.test(s) ? "~" + uuidToB64(s) : s;
      })
      .join(",");
  }

  // Single UUID or typed uuid field
  if (fieldType === "ID" || fieldType === "uuid" || UUID_RE.test(v)) {
    return "~" + uuidToB64(v);
  }

  return v;
}

function decodeValue(v: string): string {
  if (!v) return v;
  const t = v.trim();

  if (t.startsWith("[") && t.endsWith("]")) {
    try {
      const arr = JSON.parse(t) as unknown[];
      const dec = arr.map((x) =>
        typeof x === "string" && x.startsWith("~") ? b64ToUuid(x.slice(1)) : x,
      );
      return JSON.stringify(dec);
    } catch {
      // fall through
    }
  }

  if (v.includes(",")) {
    return v
      .split(",")
      .map((p) => (p.startsWith("~") ? b64ToUuid(p.slice(1)) : p))
      .join(",");
  }

  if (v.startsWith("~")) return b64ToUuid(v.slice(1));

  return v;
}

// ---------- Stable tiny IDs (generated on parse) ----------
function makeStableId(tuple: Packed, i: number): string {
  const s = `${tuple[0]}|${tuple[1]}|${tuple[2]}|${tuple[3]}|${tuple[4]}|${i}`;
  return fnv1a32(s).toString(36);
}

function nameIndex() {
  const names: string[] = [];
  const positions = new Map<string, number>();
  const indexOf = (name: string) => {
    let position = positions.get(name);
    if (position == null) {
      position = names.length;
      names.push(name);
      positions.set(name, position);
    }
    return position;
  };
  return { names, indexOf };
}

const lookup = (names: readonly string[], idx: number, kind: string): string => {
  const name = names[idx];
  if (name === undefined) throw new Error(`Bad ${kind} idx`);
  return name;
};

export function serializeEntityFilters(filters: CodecFilter[]): string {
  if (!filters || filters.length === 0) return "";

  const fields = nameIndex();
  const types = nameIndex();
  const operators = nameIndex();
  const packed: Packed[] = filters.map((f) => [
    fields.indexOf(f.field),
    types.indexOf(f.fieldType),
    operators.indexOf(f.operator),
    f.active ? 1 : 0,
    encodeValue(f.value, f.fieldType, f.operator),
  ]);

  const header: Header = { f: fields.names, t: types.names, o: operators.names };
  return encodeAdaptive(JSON.stringify([V, header, packed]));
}

export function parseEntityFilters(s: string): CodecFilter[] {
  if (!s) return [];
  const [ver, header, packed] = JSON.parse(decodeAdaptive(s)) as [number, Header, Packed[]];
  if (ver !== 1 && ver !== V) return [];

  const fields = header?.f ?? [];
  const types = ver === 1 ? [...V1_TYPES, ...(header?.t ?? [])] : (header?.t ?? []);
  const operators = ver === 1 ? V1_OPERATORS : (header?.o ?? []);

  return packed.map((t, i) => {
    const [fIdx, tIdx, oIdx, a, v] = t;
    return {
      field: lookup(fields, fIdx, "field"),
      fieldType: lookup(types, tIdx, "type"),
      operator: lookup(operators, oIdx, "operator"),
      value: decodeValue(v),
      active: a === 1,
      id: makeStableId(t, i),
    };
  });
}
